/**
 * Tracks the session whose PDF report we're still waiting on.
 *
 * The PodSessionReport event lands *after* the session has ended, by which
 * point the pod has cleared its session and — for non-guests — bounced through
 * the OIDC signout redirect, tearing the SPA down completely. So the id of the
 * session we're waiting on has to outlive both a hard refresh and a full page
 * load: localStorage, not React state or sessionStorage.
 *
 * Only that id is persisted. The report itself — status and pdfUrl — is held in
 * memory for as long as it's on screen and is never written to storage.
 *
 * There is no expiry: the pod keeps waiting until the report arrives, a new
 * session starts, or the user closes it.
 */

import type {
  PodSessionReportPayload,
  PodSessionReportStatus,
} from "../dtos/websocket";
import { logSecretly } from "./logSecretly";

const STORAGE_KEY = "pod_previous_session";

/** "PENDING" is local — the wait before the backend's status arrives. */
export type PodSessionReportState = "PENDING" | PodSessionReportStatus;

/** The only thing that survives a reload: which session owes us a report. */
interface AwaitingSession {
  /** Always compared as a string. */
  sessionId: string;
  /**
   * Guests never go through the OIDC signout redirect, so nothing tears the
   * SPA down for them. The pod is reset when they close the report instead,
   * and this is how that screen knows to do it.
   */
  isGuest: boolean;
}

/** What the UI renders — the awaiting session plus the report, once it lands. */
export interface PodSessionReport extends AwaitingSession {
  state: PodSessionReportState;
  pdfUrl: string | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

// The report itself lives here, not in storage — it is deliberately lost on reload.
let report: PodSessionReport | null = null;

// getSnapshot() must return a referentially stable value between changes or
// useSyncExternalStore re-renders forever — cache the parse, keyed on the raw string.
let cachedRaw: string | null = null;
let cachedAwaiting: PodSessionReport | null = null;

const notify = (): void => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      logSecretly("podSessionReport listener failed:", err);
    }
  });
};

const readAwaiting = (): AwaitingSession | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return parsed?.sessionId ? (parsed as AwaitingSession) : null;
  } catch {
    return null;
  }
};

const writeAwaiting = (awaiting: AwaitingSession | null): void => {
  try {
    if (awaiting) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(awaiting));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    logSecretly("Failed to persist previous pod session:", err);
  }
};

/**
 * What the UI should show: the report once it has arrived, otherwise the
 * pending wait rebuilt from the persisted session id, otherwise nothing.
 */
export const getPodSessionReport = (): PodSessionReport | null => {
  if (report) return report;

  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw !== cachedRaw) {
    cachedRaw = raw;
    const awaiting = readAwaiting();
    cachedAwaiting = awaiting
      ? { ...awaiting, state: "PENDING", pdfUrl: null }
      : null;
  }

  return cachedAwaiting;
};

/** Subscribe to changes, including writes from another tab. */
export const subscribePodSessionReport = (listener: Listener): (() => void) => {
  listeners.add(listener);

  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === STORAGE_KEY) listener();
  };
  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

/**
 * Record that a session just ended and its report is on the way. Call this
 * while the session id is still available — after logout it is gone.
 */
export const markSessionAwaitingReport = (
  sessionId: string | number | undefined | null,
  options: { isGuest?: boolean } = {}
): void => {
  if (sessionId === undefined || sessionId === null || sessionId === "") return;

  report = null;
  writeAwaiting({
    sessionId: String(sessionId),
    isGuest: Boolean(options.isGuest),
  });
  notify();
};

/**
 * Apply an incoming PodSessionReport payload.
 * Ignored unless it targets the session we're actually waiting on.
 * @returns whether the payload was applied.
 */
export const applyPodSessionReport = (
  payload: PodSessionReportPayload
): boolean => {
  const awaiting = readAwaiting();

  if (!awaiting) {
    logSecretly("Ignoring PodSessionReport — no previous session on record.");
    return false;
  }

  if (String(payload?.sessionId) !== awaiting.sessionId) {
    logSecretly(
      `Ignoring PodSessionReport for a different session. Expected: ${awaiting.sessionId}, Got: ${payload?.sessionId}`
    );
    return false;
  }

  report = {
    sessionId: awaiting.sessionId,
    isGuest: awaiting.isGuest,
    state: payload.status,
    pdfUrl: payload.status === "READY" ? payload.pdfUrl ?? null : null,
  };
  // We're no longer awaiting anything, so the persisted id has done its job:
  // drop it, which also makes a late duplicate a no-op (no record to match).
  writeAwaiting(null);
  notify();
  return true;
};

export const clearPodSessionReport = (): void => {
  report = null;
  writeAwaiting(null);
  notify();
};
